<section class="pss-management-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ title() }}</h1>
        <span>{{ description() }}</span>
      </div>
    </header>

    <div class="stats-grid">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>
              {{ card.value }}
              @if (card.suffix) {
                <small>{{ card.suffix }}</small>
              }
            </strong>
          </div>
        </article>
      }
    </div>

    <section class="table-card app-card">
      <div class="filter-grid">
        <label class="filter-field">
          <span>Search PSS</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Enter PSS name / code"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>

        <label class="filter-field">
          <span>State</span>
          <select class="app-form-select" [value]="selectedState()" (change)="updateState($any($event.target).value)">
            @for (option of filters().state; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>District</span>
          <select class="app-form-select" [value]="selectedDistrict()" (change)="updateDistrict($any($event.target).value)">
            @for (option of filters().district; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Voltage Level</span>
          <select class="app-form-select" [value]="selectedVoltageLevel()" (change)="updateVoltageLevel($any($event.target).value)">
            @for (option of filters().voltageLevelKv; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select class="app-form-select" [value]="selectedStatus()" (change)="updateStatus($any($event.target).value)">
            @for (option of filters().status; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <div class="capacity-field">
          <span>Capacity Range (MW)</span>
          <div class="capacity-inputs">
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Min"
              [value]="minCapacity()"
              (input)="updateMinCapacity($any($event.target).value)"
            >
            <span class="capacity-divider">-</span>
            <input
              class="app-form-input"
              type="number"
              min="0"
              placeholder="Max"
              [value]="maxCapacity()"
              (input)="updateMaxCapacity($any($event.target).value)"
            >
          </div>
        </div>

        <label class="filter-field">
          <span>QCA</span>
          <select class="app-form-select" [value]="selectedQca()" (change)="updateQca($any($event.target).value)">
            @for (option of filters().qca; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <div class="filter-actions">
          <button type="button" class="app-button app-button--primary">Search</button>
          <button type="button" class="app-button app-button--secondary" (click)="resetFilters()">Reset</button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="pss-table">
          <thead>
            <tr>
              <th>PSS Code</th>
              <th>PSS Name</th>
              <th>QCA Name</th>
              <th>State</th>
              <th>District</th>
              <th>Voltage Level (kV)</th>
              <th>Installed Capacity (MW)</th>
              <th>No. of Feeders</th>
              <th>No. of Generators</th>
              <th>No. of Meters</th>
              <th>Status</th>
              <th>Commission Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedRows(); track row.code) {
              <tr>
                <td><a [routerLink]="['/pss-dashboard', row.code]">{{ row.code }}</a></td>
                <td><a [routerLink]="['/pss-dashboard', row.code]">{{ row.name }}</a></td>
                <td><a [routerLink]="['/qca-dashboard']" [queryParams]="{ qcaCode: row.qcaCode }">{{ row.qcaName }} ({{ row.qcaCode }})</a></td>
                <td>{{ row.state }}</td>
                <td>{{ row.district }}</td>
                <td>{{ row.voltageLevelKv }}</td>
                <td>{{ formatCapacity(row.installedCapacityMw) }}</td>
                <td>{{ row.feederCount }}</td>
                <td>{{ row.generatorCount }}</td>
                <td>{{ row.meterCount }}</td>
                <td>
                  <span class="status-text" [class.inactive]="row.status === 'Inactive'">
                    {{ row.status }}
                  </span>
                </td>
                <td>{{ row.commissionDate }}</td>
                <td>
                  <button type="button" class="manage-button" (click)="openManageGeneratingCompaniesModal(row.code)">
                    Manage Generating Companies
                  </button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="13" class="empty-state">No PSS records found for the selected filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button
              type="button"
              class="page-button"
              [class.active]="page === currentPage()"
              (click)="goToPage(page)"
            >
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>

    @if (isLinkedCompaniesModalOpen() && selectedPss()) {
      <div class="modal-backdrop" role="presentation">
        <div class="linked-companies-modal app-card" role="dialog" aria-modal="true" aria-labelledby="linked-companies-title">
          <div class="modal-header">
            <div>
              <h2 id="linked-companies-title">Manage Generating Companies for {{ selectedPss()?.code }} - {{ selectedPss()?.name }}</h2>
            </div>
            <button type="button" class="modal-close" (click)="closeManageGeneratingCompaniesModal()" aria-label="Close linked companies modal">×</button>
          </div>

          <div class="modal-tabs">
            <button
              type="button"
              class="tab-button"
              [class.active]="activeCompanyTab() === 'available'"
              (click)="switchCompanyTab('available')"
            >
              Available Generating Companies
            </button>
            <button
              type="button"
              class="tab-button"
              [class.active]="activeCompanyTab() === 'linked'"
              (click)="switchCompanyTab('linked')"
            >
              Linked Generating Companies ({{ linkedCompanies().length }})
            </button>
          </div>

          <div class="modal-body">
            @if (activeCompanyTab() === 'available') {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search available companies..."
                    [value]="availableCompanySearch()"
                    (input)="updateAvailableCompanySearch($any($event.target).value)"
                  >
                </label>

                <button
                  type="button"
                  class="add-selected-button"
                  [disabled]="selectedAvailableCompanyNos().length === 0"
                  (click)="addSelectedCompanies()"
                >
                  Add Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Registration No</th>
                      <th>Entity Name</th>
                      <th>Username</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredAvailableCompanies(); track item.registrationNo) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedAvailableCompanyNos().includes(item.registrationNo)"
                            (change)="toggleAvailableCompanySelection(item.registrationNo, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.registrationNo }}</td>
                        <td>{{ item.entityName }}</td>
                        <td>{{ item.username }}</td>
                        <td>{{ item.email }}</td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="5" class="empty-state">No available generating companies found for this PSS.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search linked companies..."
                    [value]="linkedCompanySearch()"
                    (input)="updateLinkedCompanySearch($any($event.target).value)"
                  >
                </label>

                <button
                  type="button"
                  class="remove-selected-button"
                  [disabled]="selectedLinkedCompanyNos().length === 0"
                  (click)="removeSelectedCompanies()"
                >
                  Remove Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Registration No</th>
                      <th>Entity Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredLinkedCompanies(); track item.registrationNo) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedLinkedCompanyNos().includes(item.registrationNo)"
                            (change)="toggleLinkedCompanySelection(item.registrationNo, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.registrationNo }}</td>
                        <td>{{ item.entityName }}</td>
                        <td>{{ item.username }}</td>
                        <td>{{ item.email }}</td>
                        <td>
                          <button type="button" class="remove-icon-button" (click)="removeLinkedCompany(item.registrationNo)" aria-label="Remove linked generating company">🗑</button>
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="6" class="empty-state">No linked generating companies found for this PSS.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>

          <div class="modal-footer">
            <button type="button" class="app-button app-button--primary" (click)="closeManageGeneratingCompaniesModal()">Close</button>
          </div>
        </div>
      </div>
    }
  </div>
</section>
